import React, { useState } from 'react';
import { useJobs } from '../../hooks/useJobs';
import { Download, HardDrive } from 'lucide-react';

const INK    = '#2B2620';
const WALNUT = '#8B4513';
const OCHRE  = '#A6752A';
const STONE  = '#8C8275';
const PAPER  = '#FDFBF8';
const FONT_SANS = "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

export default function DataManagement() {
  const { jobs } = useJobs();
  const [exporting, setExporting] = useState(false);

  async function handleExportAllData() {
    setExporting(true);

    try {
      // Create comprehensive data export
      const exportData = {
        exportDate: new Date().toISOString(),
        jobs: jobs.map(job => ({
          id: job.id,
          contractNumber: job.contractNumber,
          customerName: job.customerName,
          name: job.name,
          address: job.address,
          status: job.status,
          soldDate: job.soldDate,
          scheduledDate: job.scheduledDate,
          windowCount: job.windowCount,
          doorCount: job.doorCount,
          durationDays: job.durationDays,
          assignedCrew: job.assignedCrew,
          jobType: job.jobType,
          notes: job.notes
        })),
        summary: {
          totalJobs: jobs.length,
          scheduled: jobs.filter(j => j.status === 'scheduled').length,
          completed: jobs.filter(j => j.status === 'completed').length,
          inProduction: jobs.filter(j => j.status === 'in_production').length
        }
      };

      // Create JSON file
      const blob = new Blob([JSON.stringify(exportData, null, 2)], {
        type: 'application/json'
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `twp-data-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      alert('Data exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export data. Please try again.');
    } finally {
      setExporting(false);
    }
  }

  return (
    <div style={{ fontFamily: FONT_SANS }}>
      <h3 style={{
        fontSize: '15px',
        fontWeight: '600',
        color: INK,
        marginBottom: '16px'
      }}>
        Data Management
      </h3>

      <div style={{
        padding: '20px',
        background: PAPER,
        borderRadius: '12px',
        border: '1px solid rgba(139,69,19,0.1)'
      }}>
        {/* Export Data */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{
            fontSize: '14px',
            fontWeight: '600',
            color: INK,
            marginBottom: '8px'
          }}>
            Export All Data
          </div>
          <div style={{
            fontSize: '13px',
            color: STONE,
            marginBottom: '12px'
          }}>
            Download a complete backup of all jobs and system data as JSON
          </div>
          <button
            onClick={handleExportAllData}
            disabled={exporting}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
              width: '100%',
              padding: '12px',
              background: WALNUT,
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              fontFamily: FONT_SANS,
              color: 'white',
              cursor: exporting ? 'not-allowed' : 'pointer',
              opacity: exporting ? 0.6 : 1,
              transition: 'background 0.2s'
            }}
            onMouseEnter={(e) => {
              if (!exporting) e.currentTarget.style.background = '#A0522D';
            }}
            onMouseLeave={(e) => {
              if (!exporting) e.currentTarget.style.background = WALNUT;
            }}
          >
            <Download size={15} strokeWidth={2} /> {exporting ? 'Exporting...' : 'Export Data (JSON)'}
          </button>
        </div>

        {/* Data Stats */}
        <div style={{
          padding: '16px',
          background: 'rgba(139,69,19,0.05)',
          borderRadius: '8px',
          border: '1px solid rgba(139,69,19,0.15)',
          marginBottom: '24px'
        }}>
          <div style={{
            fontSize: '13px',
            fontWeight: '600',
            color: WALNUT,
            marginBottom: '12px'
          }}>
            Database Statistics
          </div>

          <div style={{
            display: 'grid',
            gap: '8px'
          }}>
            <StatRow label="Total Jobs" value={jobs.length} />
            <StatRow label="Scheduled" value={jobs.filter(j => j.status === 'scheduled').length} />
            <StatRow label="In Production" value={jobs.filter(j => j.status === 'in_production').length} />
            <StatRow label="Completed" value={jobs.filter(j => j.status === 'completed').length} />
          </div>
        </div>

        {/* Backup Info */}
        <div style={{
          padding: '16px',
          background: `${OCHRE}0d`,
          borderRadius: '8px',
          border: `1px solid ${OCHRE}33`
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '8px'
          }}>
            <HardDrive size={15} strokeWidth={2} color={OCHRE} />
            <div style={{
              fontSize: '13px',
              fontWeight: '600',
              color: OCHRE
            }}>
              Automatic Backups
            </div>
          </div>
          <div style={{
            fontSize: '12px',
            color: STONE,
            lineHeight: '1.5'
          }}>
            Your data is automatically backed up to Firebase Firestore in real-time.
            All changes are synced instantly and stored securely in the cloud.
          </div>
        </div>
      </div>
    </div>
  );
}

function StatRow({ label, value }) {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      fontSize: '12px'
    }}>
      <span style={{ color: STONE }}>{label}</span>
      <span style={{ fontWeight: '600', color: INK }}>{value}</span>
    </div>
  );
}
